/**
 * 侧栏分组名单（T5 菜单分块机制）—— 业务域驱动
 * ────────────────────────────────────────────────────────────────────
 * 本文件是「菜单分块」机制里**唯一需要改的件**。匹配优先级、兜底、空组过滤、
 * 保序等全部逻辑在 utils/sidebarGrouping.ts 里，与本名单完全解耦。
 *
 * ⚠️ 为什么不再手写 path / 中文关键词（2026-10-15 修）
 *   一级菜单在 convertBackendMenusToProLayout 里**不写 path**（path 只在 level≥2
 *   才赋值，见 utils/backendMenuNavigation.ts），一级项只有 code 与 key
 *   （key = menu.path || menu.code || id）。所以旧名单里的 paths 前缀对一级菜单
 *   永不命中、codes 又全空，实际只有 names 的中文子串在干活 —— 结果 19 条一级
 *   菜单有 7 条掉进「其他」，连「概览」都整组为空。现在改成业务域驱动：
 *
 *   业务域（= 后端一级菜单 code = src/pages/modules 下的模块目录名）
 *     → 一处声明，自动展开成 codes（第一优先级，语言无关、不受改名影响）
 *       与 paths（第二优先级，前缀匹配 /sales 这类形态）
 *
 * 匹配优先级（见 utils/sidebarGrouping.ts）：
 *   1. code 精确匹配（业务域，主力）
 *   2. path 前缀匹配（最长前缀优先；按 `/xxx` 或 `/xxx/...` 边界匹配）
 *   3. name 归一化包含（去空格 + 转小写后子串匹配，现网菜单名兜底）
 *   4. 兜底组（isFallback: true）
 *
 * 🔒 硬约束：未命中任何分组的菜单项**必须**落进兜底组，绝不丢弃。
 *    菜单即权限入口，丢菜单等于丢功能。
 *
 * 🆕 后续加菜单怎么归类（见 utils/sidebarGroups.coverage.regression.test.cjs）
 *   · 加在**已有业务域**下（如 /sales 里再加一个菜单）→ 零配置，自动进对应组
 *   · 加一个**新业务域**（如 /crm）→ 落「其他」不丢，且覆盖率测试红灯点名，
 *     补一行 domains 即可 —— 不会静默漏掉
 */

export interface SidebarGroupMatch {
  /** 业务域精确匹配（后端一级菜单 code），优先级最高 */
  codes?: string[];
  /** 路由 path 前缀（最长前缀优先） */
  paths?: string[];
  /** 菜单显示名（归一化后子串包含），仅作现网菜单名兜底 */
  names?: string[];
}

export interface SidebarGroup {
  /** 分组唯一标识（用于渲染 key 与调试） */
  key: string;
  /** 分组标题（侧栏上显示的文字） */
  title: string;
  /** 命中规则；兜底组不需要 */
  match?: SidebarGroupMatch;
  /** 是否为兜底组：只收未被其它组命中的菜单项，不参与前三档匹配 */
  isFallback?: boolean;
}

/**
 * 分组声明 —— 只维护「组 + 业务域」这一层，codes / paths 自动展开。
 * 组顺序 = 数组顺序 = 侧栏从上到下的顺序。
 */
export interface SidebarGroupSpec {
  key: string;
  title: string;
  /** 业务域：后端一级菜单 code，同时也是 src/pages/modules 下的模块目录名 */
  domains: string[];
  /** 现网菜单显示名兜底：后端没给 code、key 也不是 path 时按名字认领 */
  names?: string[];
}

/**
 * ── 分区依据（2026-10-29 增加数据处理中心）────────────────────────────
 * 从上到下：
 *   概览与决策 → 基础与配置 → 数据处理中心 → 贸易业务 → 仓储与履约 → 财务与资金
 *   → 组织与其他 → AI 工具（置底）
 *
 * 与上一版的差别（记下来，免得来回改）：
 *   ·「贸易业务」只留成交链的前半段：询报价 → 销售 → 采购。
 *     销售与采购**故意不拆**（不同于 SAP 的 SD/MM 分家）——这家公司是一个
 *     跟单员从询价跟到收汇，拆开等于把单据链拦腰切断。
 *   ·「仓储与履约」接过链的后半段：库存/仓库（货权）+ 物流/单证（出运与关务），
 *     即 manual 里的实物流与单证流。
 *   ·「基础与配置」把主数据（基础档案）与后台（系统/流程/邮箱）合成一组。
 *   ·「组织与其他」= 人力资源 + 研发管理。
 *   · AI 工具单独置底（见下一段）。
 *
 * 🤖 关于「AI 工具」组：/ai-model 在路由上**是一个一级菜单**，底下挂
 *    OCR 转 Excel / Excel 整合工具 / PDF加密转换 / 模型助手 / 物料临时库存表。
 *    分组按业务域认领，所以不管后端把它们挂成 1 个一级菜单（组里 1 个入口，
 *    子项在弹出面板里）还是多个一级菜单（组里并排多个入口），都会落进这一组。
 */
export const SIDEBAR_GROUP_SPECS: SidebarGroupSpec[] = [
  {
    // 概览组曾经整组为空：旧关键词只有 概览/首页/仪表盘/工作台，
    // 而现网这两个菜单叫「智能看板」「报表中心」，一个都没命中
    key: 'overview',
    title: '概览与决策',
    domains: ['dashboard', 'report'],
    names: ['智能看板', '报表中心', '驾驶舱', '首页', '工作台'],
  },
  {
    // 主数据 + 后台配置：基础档案与系统/流程/邮箱同桌
    key: 'base-config',
    title: '基础与配置',
    domains: ['master-data', 'system', 'process', 'email'],
    names: ['基础档案', '系统管理', '流程管理', '公共邮箱'],
  },
  {
    key: 'data-processing',
    title: '数据处理中心',
    domains: ['data-center'],
    names: ['数据管理中心'],
  },
  {
    // 成交链前半段：询报价 → 销售 → 采购（公司主业务流程，不拆销售与采购）
    key: 'trade',
    title: '贸易业务',
    domains: ['inquiry-quote', 'sales', 'procurement'],
    names: ['询报价管理', '销售管理', '采购管理'],
  },
  {
    // 履约段：货权（库存/仓库）+ 出运与关务（物流/单证）
    key: 'fulfillment',
    title: '仓储与履约',
    domains: ['inventory', 'warehouse', 'logistics', 'document'],
    names: ['库存管理', '仓库管理', '物流管理', '单证管理'],
  },
  {
    // 资金流。invoice-management（进项税发票库存/退税，单证流的枢纽）暂无一级菜单，
    // 先认领归属，将来后端一出一级菜单即自动归位到这里
    key: 'finance',
    title: '财务与资金',
    domains: ['finance', 'cash', 'cost', 'invoice-management'],
    names: ['财务管理', '现金管理', '成本管理', '发票管理'],
  },
  {
    // 人力资源现挂在 general（general/hr）下，故业务域写 general
    key: 'org-other',
    title: '组织与其他',
    domains: ['general', 'rd'],
    names: ['人力资源', '研发管理'],
  },
  {
    // 置底。名字归一化会去掉所有空白，所以 'OCR 转 Excel' 与 'OCR转excel'
    // 归一化后是同一个串，不必写两遍
    key: 'ai-tools',
    title: 'AI 工具',
    domains: ['ai-model'],
    names: [
      'AI模型',
      '模型助手',
      'OCR 转 Excel',
      'Excel 整合工具',
      'PDF加密转换',
      '物料临时库存表',
    ],
  },
];

/** 兜底组 key（侧栏渲染时用它区分「其他」） */
export const SIDEBAR_FALLBACK_GROUP_KEY = 'other';

/**
 * 兜底组 —— 未命中任何分组的菜单项一律进这里。
 * 即使 SIDEBAR_GROUPS 里没有它，sidebarGrouping 也会自动补一个同名兜底组，
 * 保证「绝不丢菜单」这条硬约束在任何名单下都成立。
 */
export const SIDEBAR_FALLBACK_GROUP: SidebarGroup = {
  key: SIDEBAR_FALLBACK_GROUP_KEY,
  title: '其他',
  isFallback: true,
};

/** 声明 → 运行时名单：domains 一处展开成 codes 与 paths，避免两处事实来源 */
function expandSpecs(specs: SidebarGroupSpec[]): SidebarGroup[] {
  return specs.map((spec) => ({
    key: spec.key,
    title: spec.title,
    match: {
      codes: [...spec.domains],
      paths: spec.domains.map((domain) => `/${domain}`),
      ...(spec.names && spec.names.length > 0 ? { names: [...spec.names] } : {}),
    },
  }));
}

export const SIDEBAR_GROUPS: SidebarGroup[] = [
  ...expandSpecs(SIDEBAR_GROUP_SPECS),
  // ⚠️ 兜底组固定在数组末尾（组顺序 = 数组顺序，兜底组永远排最后）
  SIDEBAR_FALLBACK_GROUP,
];

/** 已被认领的业务域（覆盖率回归测试用：模块目录必须在这里或白名单里） */
export const SIDEBAR_MAPPED_DOMAINS: string[] = SIDEBAR_GROUP_SPECS.flatMap(
  (spec) => spec.domains,
);

export default SIDEBAR_GROUPS;
